import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  MapPin, Fingerprint, LogIn, LogOut, ShieldCheck, Wifi,
  CalendarDays, Clock3, Users, AlertTriangle, Settings2,
  CheckCircle2, XCircle, Plane, ChevronLeft, Building2, Star,
  Plus, Trash2, Download, Film,
} from "lucide-react";
import { toast } from "sonner";

/* Attendance page ─────────────────────────────────────────────────────────
 *  Tabs:
 *    · Punch      — my day: big Punch In/Out button, method chips, history
 *    · Team       — HR/admin only: table of who's in, out, absent
 *    · Leaves     — my leaves + HR queue (if HR)
 *    · Settings   — HR/admin only: office coords + IP whitelist + radius
 *
 *  Auto-attendance:
 *    On mount, we ping /office/attendance/context. If the caller is on a
 *    whitelisted office IP AND has no punch today, we punch in
 *    automatically (idempotent).
 * ------------------------------------------------------------------------ */

const TABS = [
  { key: "punch",    label: "My Day",   icon: Fingerprint, roles: "*" },
  { key: "team",     label: "Team",     icon: Users,       roles: "hr" },
  { key: "leaves",   label: "Leaves",   icon: Plane,       roles: "*" },
  { key: "settings", label: "Settings", icon: Settings2,   roles: "hr" },
];

export default function AgencyAttendance() {
  const [ctx, setCtx] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState("punch");
  const [autoRan, setAutoRan] = useState(false);

  const isHR = !!cfg?.can_edit;

  const loadCtx = useCallback(async () => {
    const { data } = await api.get("/office/attendance/context");
    setCtx(data);
  }, []);
  const loadCfg = useCallback(async () => {
    const { data } = await api.get("/office/config");
    setCfg(data);
  }, []);

  useEffect(() => {
    loadCtx();
    loadCfg();
  }, [loadCtx, loadCfg]);

  // Auto-punch-in when on office IP + no punch yet today
  useEffect(() => {
    if (!ctx || autoRan) return;
    if (ctx.on_office_ip && !ctx.today_ping) {
      setAutoRan(true);
      (async () => {
        try {
          const { data } = await api.post("/office/attendance/punch", { method: "ip" });
          if (data.ok && data.direction === "in") {
            toast.success("Auto-attendance recorded (office IP detected)");
          }
          loadCtx();
        } catch { /* silent */ }
      })();
    } else {
      setAutoRan(true);
    }
  }, [ctx, autoRan, loadCtx]);

  const visibleTabs = TABS.filter((t) => t.roles === "*" || isHR);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8"
         data-testid="agency-attendance-page">

      <Link to="/dashboard"
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-white/50 hover:text-white transition"
            data-testid="attendance-back-link">
        <ChevronLeft size={12} /> Back to Office
      </Link>
      <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
            Agency Office · Attendance
          </div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-1">
            Punch In
          </h1>
        </div>
        {cfg?.office_address_label && (
          <div className="text-[11px] text-white/45">
            <MapPin size={11} className="inline mr-1" /> {cfg.office_address_label}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-1 border-b border-white/10 overflow-x-auto"
           data-testid="attendance-tabs">
        {visibleTabs.map((t) => {
          const active = tab === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={"px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase font-semibold border-b-2 transition inline-flex items-center gap-1.5 " +
                (active ? "border-white text-white" : "border-transparent text-white/50 hover:text-white/80")}
              data-testid={`attendance-tab-${t.key}`}
            >
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        {tab === "punch"    && <PunchPanel ctx={ctx} onPunched={loadCtx} />}
        {tab === "team"     && <TeamPanel />}
        {tab === "leaves"   && <LeavesPanel isHR={isHR} />}
        {tab === "settings" && <SettingsPanel cfg={cfg} onSaved={loadCfg} />}
      </div>
      {tab === "settings" && isHR && <BranchesPanel />}
    </div>
  );
}

/* ---- Punch panel --------------------------------------------------------- */

function PunchPanel({ ctx, onPunched }) {
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState("ip");
  const [note, setNote] = useState("");

  const loadHist = useCallback(async () => {
    const { data } = await api.get("/office/attendance/me/history?days=30");
    setHistory(data.pings || []);
  }, []);
  useEffect(() => { loadHist(); }, [loadHist]);

  const p = ctx?.today_ping;
  const state = !p ? "not_punched"
              : p.punch_out_at ? "punched_out"
              : "punched_in";

  const punch = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const body = { method, note };
      if (method === "geo") {
        try {
          const pos = await new Promise((res, rej) =>
            navigator.geolocation.getCurrentPosition(res, rej,
              { enableHighAccuracy: true, timeout: 8000 })
          );
          body.lat = pos.coords.latitude;
          body.lng = pos.coords.longitude;
        } catch {
          toast.error("Couldn't get your location. Try 'Office IP' or ask HR for a manual override.");
          setBusy(false); return;
        }
      }
      const { data } = await api.post("/office/attendance/punch", body);
      const dir = data.direction;
      if (dir === "in")             toast.success("Punched in ✓");
      else if (dir === "out")       toast.success("Punched out ✓ · have a good evening");
      else                          toast.info("Already punched out for today");
      setNote("");
      await onPunched();
      await loadHist();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Punch failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5" data-testid="attendance-punch-panel">
      {/* LEFT — big status card */}
      <div className="lg:col-span-2 cs-card rounded-2xl p-6"
           data-testid="attendance-status-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
              {ctx?.date || "Today"}
            </div>
            <div className="mt-1 font-display text-3xl uppercase tracking-tight">
              {state === "not_punched"  && "Not punched yet"}
              {state === "punched_in"   && "Punched in"}
              {state === "punched_out"  && "Day wrapped"}
            </div>
          </div>
          {state !== "not_punched" && p && (
            <div className="text-right">
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">In / Out</div>
              <div className="mt-1 text-sm text-white/85 tabular-nums" data-testid="attendance-times">
                {fmtT(p.punch_in_at)}
                {p.punch_out_at ? ` → ${fmtT(p.punch_out_at)}` : " → —"}
              </div>
              {p.punch_out_at && (
                <div className="text-[11px] text-white/50 mt-0.5">
                  {mins(p.punch_in_at, p.punch_out_at)} on desk
                </div>
              )}
            </div>
          )}
        </div>

        {/* Method picker */}
        {state !== "punched_out" && (
          <div className="mt-6">
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/55 mb-2">
              How
            </div>
            <div className="flex flex-wrap gap-2">
              <MethodPill k="ip"     label="Office IP"  icon={Wifi}
                          active={method === "ip"}     on={() => setMethod("ip")} />
              <MethodPill k="geo"    label="Location"   icon={MapPin}
                          active={method === "geo"}    on={() => setMethod("geo")}
                          disabled={!ctx?.has_office_geo} />
              <MethodPill k="manual" label="Manual (HR)" icon={ShieldCheck}
                          active={method === "manual"} on={() => setMethod("manual")} />
            </div>
            {method === "manual" && (
              <textarea value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Reason for manual override (required · e.g. off-site client meeting)"
                className="mt-3 w-full h-20 rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-sm resize-none"
                data-testid="attendance-manual-note" />
            )}
            {method === "ip" && (
              <div className={"mt-3 text-[11px] " + (ctx?.on_office_ip ? "text-emerald-300" : "text-amber-300")}>
                {ctx?.on_office_ip
                  ? <><CheckCircle2 size={11} className="inline mr-1" /> Office IP detected · {ctx?.client_ip}</>
                  : <><AlertTriangle size={11} className="inline mr-1" /> Not on office IP · {ctx?.client_ip}</>}
              </div>
            )}
            {method === "geo" && !ctx?.has_office_geo && (
              <div className="mt-3 text-[11px] text-amber-300">
                <AlertTriangle size={11} className="inline mr-1" />
                Office coordinates not set yet. Ask HR to configure them in Settings.
              </div>
            )}
          </div>
        )}

        {/* Action button */}
        <div className="mt-6 flex items-center gap-3">
          {state !== "punched_out" && (
            <button
              onClick={punch}
              disabled={busy}
              className="h-11 px-6 rounded-full font-semibold text-[12px] tracking-[0.18em] uppercase bg-white text-black hover:bg-white/90 disabled:opacity-60 inline-flex items-center gap-2"
              data-testid="attendance-punch-btn"
            >
              {state === "not_punched"
                ? <><LogIn size={14} /> Punch In</>
                : <><LogOut size={14} /> Punch Out</>}
            </button>
          )}
          {state === "punched_out" && (
            <div className="text-[11px] text-white/55">
              Day locked. Come back tomorrow ✨
            </div>
          )}
        </div>

        {/* Details when punched */}
        {p && (
          <div className="mt-6 pt-5 border-t border-white/8 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[11.5px] text-white/60">
            <div><span className="text-white/40">In method  ·</span> {p.method_in}{p.in_reason ? ` — ${p.in_reason}` : ""}</div>
            {p.punch_out_at && (
              <div><span className="text-white/40">Out method ·</span> {p.method_out}{p.out_reason ? ` — ${p.out_reason}` : ""}</div>
            )}
            {p.note && (
              <div className="sm:col-span-2"><span className="text-white/40">Note ·</span> {p.note}</div>
            )}
          </div>
        )}
      </div>

      {/* RIGHT — 30-day history */}
      <div className="cs-card rounded-2xl p-5" data-testid="attendance-history-card">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
          Last 30 days
        </div>
        <div className="mt-1 font-display text-xl uppercase tracking-tight">
          History
        </div>
        {history.length === 0 ? (
          <div className="mt-5 text-[12px] text-white/40">Nothing yet. Punch in above to start.</div>
        ) : (
          <ul className="mt-4 divide-y divide-white/6">
            {history.map((h) => (
              <li key={h.id}
                  className="py-2.5 flex items-center justify-between text-[12px]"
                  data-testid={`attendance-history-row-${h.date}`}>
                <div>
                  <div className="text-white/85 font-semibold">{h.date}</div>
                  <div className="text-white/45 text-[10.5px] tabular-nums">
                    {fmtT(h.punch_in_at)} → {h.punch_out_at ? fmtT(h.punch_out_at) : "—"}
                  </div>
                </div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-white/50">
                  {h.punch_out_at ? mins(h.punch_in_at, h.punch_out_at) : "open"}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MethodPill({ label, icon: Icon, active, on, disabled }) {
  return (
    <button
      type="button"
      onClick={on}
      disabled={disabled}
      className={"px-3 h-9 rounded-full text-[10.5px] tracking-[0.18em] uppercase font-semibold inline-flex items-center gap-1.5 transition " +
        (disabled ? "opacity-40 cursor-not-allowed border border-white/10 text-white/45" :
         active   ? "bg-white text-black" :
                    "border border-white/20 text-white/70 hover:text-white hover:border-white")}
      data-testid={`attendance-method-${label.split(" ")[0].toLowerCase()}`}
    >
      <Icon size={12} /> {label}
    </button>
  );
}

/* ---- Team panel ---------------------------------------------------------- */

function TeamPanel() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);

  useEffect(() => {
    api.get("/office/branches").then(({ data }) => setBranches(data.branches || []));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ date });
      if (branchId) q.set("branch_id", branchId);
      const { data } = await api.get(`/office/attendance/team?${q}`);
      setRows(data.rows || []);
    } finally { setLoading(false); }
  }, [date, branchId]);
  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    const s = { in: 0, out: 0, on_shoot: 0, absent: 0 };
    rows.forEach((r) => { s[r.status] = (s[r.status] || 0) + 1; });
    return s;
  }, [rows]);

  const downloadPdf = async () => {
    setDlBusy(true);
    try {
      // 7-day window ending on `date`
      const to = date;
      const from = new Date(new Date(date) - 6 * 86400000).toISOString().slice(0, 10);
      const q = new URLSearchParams({ from_date: from, to_date: to });
      if (branchId) q.set("branch_id", branchId);
      const res = await api.get(`/office/attendance/export.pdf?${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_${from}_${to}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Attendance sheet downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally { setDlBusy(false); }
  };

  return (
    <div className="cs-card rounded-2xl p-5" data-testid="attendance-team-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
            Team snapshot
          </div>
          <div className="mt-1 font-display text-2xl uppercase tracking-tight">
            {rows.length ? `${rows.length} teammates` : "Team"}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {branches.length > 1 && (
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)}
                    className="bg-black/40 border border-white/15 rounded-md h-9 px-2 text-[11.5px]"
                    data-testid="team-branch-filter">
              <option value="">All branches</option>
              {branches.map((b) => {
                const optLabel = `${b.city_label} · ${b.label}`;
                return <option key={b.id} value={b.id}>{optLabel}</option>;
              })}
            </select>
          )}
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                 className="bg-black/40 border border-white/15 rounded-md px-3 h-9 text-sm"
                 data-testid="attendance-team-date" />
          <button onClick={downloadPdf} disabled={dlBusy}
            className="h-9 px-3 rounded-md text-[10.5px] tracking-[0.18em] uppercase font-semibold border border-white/25 hover:bg-white hover:text-black transition disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="team-download-pdf">
            <Download size={12} /> {dlBusy ? "…" : "PDF · 7 days"}
          </button>
        </div>
      </div>

      <div className="mt-4 flex gap-2 flex-wrap">
        <Chip label={`In · ${summary.in}`}     tone="ok"  />
        {summary.on_shoot > 0 && <Chip label={`On Shoot · ${summary.on_shoot}`} tone="film" />}
        <Chip label={`Out · ${summary.out}`}   tone="mid" />
        <Chip label={`Absent · ${summary.absent}`} tone="warn" />
      </div>

      {loading ? (
        <div className="mt-6 text-sm text-white/45">Loading…</div>
      ) : (
        <div className="mt-5 space-y-1.5">
          {rows.map((r) => (
            <div key={r.user.id}
                 className="flex items-center justify-between gap-3 py-2 border-b border-white/6"
                 data-testid={`attendance-team-row-${r.user.id}`}>
              <div className="min-w-0">
                <div className="text-[13px] text-white/90 truncate">{r.user.name}</div>
                <div className="text-[10.5px] text-white/45 truncate">
                  {r.user.agency_role || r.user.primary_role} · {r.user.email}
                </div>
              </div>
              {r.status === "absent" ? (
                <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.2em] uppercase bg-amber-500/15 text-amber-300 font-semibold">
                  Absent
                </span>
              ) : r.status === "on_shoot" ? (
                <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.2em] uppercase bg-indigo-500/20 text-indigo-200 font-semibold inline-flex items-center gap-1"
                      data-testid={`team-onshoot-${r.user.id}`}>
                  <Film size={10} /> On Shoot
                </span>
              ) : (
                <div className="text-right text-[10.5px] text-white/70 tabular-nums">
                  <div>{fmtT(r.ping.punch_in_at)}{r.ping.punch_out_at ? ` → ${fmtT(r.ping.punch_out_at)}` : ""}</div>
                  <div className="text-[9px] tracking-[0.2em] uppercase mt-0.5" style={{ color: r.status === "in" ? "#6EE7B7" : "#9CA3AF" }}>
                    {r.status === "in" ? "On desk" : "Wrapped"}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Chip({ label, tone }) {
  const map = {
    ok:   "bg-emerald-500/15 text-emerald-300",
    warn: "bg-amber-500/15 text-amber-300",
    mid:  "bg-white/8 text-white/70",
    film: "bg-indigo-500/15 text-indigo-300",
  };
  return (
    <span className={"px-2.5 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold " + map[tone]}>
      {label}
    </span>
  );
}

/* ---- Leaves panel -------------------------------------------------------- */

function LeavesPanel({ isHR }) {
  const [mine, setMine] = useState([]);
  const [queue, setQueue] = useState([]);
  const [subTab, setSubTab] = useState("mine");
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    const [me, q] = await Promise.all([
      api.get("/leaves/mine"),
      isHR ? api.get("/leaves/pending") : Promise.resolve({ data: { leaves: [] } }),
    ]);
    setMine(me.data.leaves || []);
    setQueue(q.data.leaves || []);
  }, [isHR]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="attendance-leaves-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex gap-1">
          <SubTab k="mine"   label="My leaves"  active={subTab === "mine"}   on={() => setSubTab("mine")}   testid="leaves-subtab-mine" />
          {isHR && (
            <SubTab k="queue" label={`HR Queue (${queue.length})`} active={subTab === "queue"} on={() => setSubTab("queue")} testid="leaves-subtab-queue" />
          )}
        </div>
        <button onClick={() => setShowForm((s) => !s)}
          className="h-9 px-4 rounded-full text-[10.5px] tracking-[0.18em] uppercase font-semibold border border-white/25 hover:bg-white hover:text-black transition inline-flex items-center gap-1.5"
          data-testid="leaves-new-btn">
          <Plane size={12} /> {showForm ? "Cancel" : "New Leave"}
        </button>
      </div>

      {showForm && <NewLeaveForm onSubmitted={() => { setShowForm(false); load(); }} />}

      {subTab === "mine" && (
        <LeaveList list={mine} readOnly testid="leaves-mine-list" />
      )}
      {subTab === "queue" && isHR && (
        <LeaveList list={queue} onDecide={load} testid="leaves-queue-list" />
      )}
    </div>
  );
}

function SubTab({ label, active, on, testid }) {
  return (
    <button onClick={on}
      className={"px-3 py-2 text-[11px] tracking-[0.18em] uppercase font-semibold rounded-lg transition " +
        (active ? "bg-white/10 text-white" : "text-white/50 hover:text-white/80")}
      data-testid={testid}>
      {label}
    </button>
  );
}

function NewLeaveForm({ onSubmitted }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    kind: "casual", from_date: today, to_date: today, reason: "",
    is_emergency: false,
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      await api.post("/leaves/request", form);
      toast.success("Leave submitted for HR review");
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally { setBusy(false); }
  };

  return (
    <div className="cs-card rounded-xl p-5 mb-5" data-testid="leaves-new-form">
      <div className="grid sm:grid-cols-4 gap-3">
        <div className="sm:col-span-2">
          <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Type</label>
          <select value={form.kind} onChange={(e) => upd("kind", e.target.value)}
            className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
            data-testid="leaves-form-kind">
            <option value="casual">Casual</option>
            <option value="sick">Sick</option>
            <option value="earned">Earned</option>
            <option value="wfh">WFH</option>
            <option value="comp_off">Comp Off</option>
            <option value="maternity">Maternity</option>
            <option value="paternity">Paternity</option>
            <option value="bereavement">Bereavement</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">From</label>
          <input type="date" value={form.from_date} onChange={(e) => upd("from_date", e.target.value)}
            className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
            data-testid="leaves-form-from" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">To</label>
          <input type="date" value={form.to_date} onChange={(e) => upd("to_date", e.target.value)}
            className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
            data-testid="leaves-form-to" />
        </div>
      </div>
      <div className="mt-3">
        <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Reason</label>
        <textarea value={form.reason} onChange={(e) => upd("reason", e.target.value)}
          className="mt-1 w-full h-20 bg-black/40 border border-white/15 rounded-md px-3 py-2 text-sm resize-none"
          placeholder="Family function, medical, personal…"
          data-testid="leaves-form-reason" />
      </div>
      <label className="mt-3 flex items-center gap-2 text-[12px] text-white/60"
             data-testid="leaves-form-emergency-wrap">
        <input type="checkbox" checked={form.is_emergency} onChange={(e) => upd("is_emergency", e.target.checked)}
               data-testid="leaves-form-emergency" />
        Emergency — flag for expedited HR review
      </label>
      <div className="mt-4 flex justify-end">
        <button onClick={submit} disabled={busy}
          className="h-10 px-6 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold bg-white text-black hover:bg-white/90 disabled:opacity-60"
          data-testid="leaves-form-submit">
          {busy ? "Sending…" : "Submit for HR"}
        </button>
      </div>
    </div>
  );
}

function LeaveList({ list, readOnly = false, onDecide, testid }) {
  if (list.length === 0)
    return <div className="cs-card rounded-xl p-8 text-center text-sm text-white/45" data-testid={testid}>Nothing here yet.</div>;

  const decide = async (id, action) => {
    const note = action === "reject"
      ? (prompt("Reason for rejection (visible to staff):", "") || "")
      : (prompt("Optional note back to staff:", "") || "");
    try {
      await api.post(`/leaves/${id}/decide`, { action, hr_note: note });
      toast.success(`Leave ${action}d`);
      onDecide?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      {list.map((l) => (
        <div key={l.id} className="cs-card rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap"
             data-testid={`leave-row-${l.id}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display text-sm uppercase tracking-tight">{l.user_name}</span>
              <span className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-white/8 text-white/70">
                {l.kind}
              </span>
              {l.is_emergency && (
                <span className="text-[9.5px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 font-semibold">
                  Emergency
                </span>
              )}
              <StatusPill status={l.status} />
            </div>
            <div className="mt-1 text-[11.5px] text-white/60">
              <CalendarDays size={11} className="inline mr-1" />
              {l.from_date} → {l.to_date}
              <span className="text-white/35"> · {l.days} day{l.days > 1 ? "s" : ""}</span>
            </div>
            <div className="mt-1 text-[12px] text-white/75 line-clamp-2">{l.reason}</div>
            {l.hr_note && (
              <div className="mt-1 text-[11px] text-white/45 italic">HR: {l.hr_note}</div>
            )}
          </div>
          {!readOnly && l.status === "pending" && (
            <div className="flex gap-2">
              <button onClick={() => decide(l.id, "approve")}
                className="h-8 px-3 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500 hover:text-black"
                data-testid={`leave-approve-${l.id}`}>
                <CheckCircle2 size={11} className="inline mr-1" /> Approve
              </button>
              <button onClick={() => decide(l.id, "reject")}
                className="h-8 px-3 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold bg-red-500/15 text-red-200 hover:bg-red-500 hover:text-black"
                data-testid={`leave-reject-${l.id}`}>
                <XCircle size={11} className="inline mr-1" /> Reject
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending:  "bg-amber-500/15 text-amber-300",
    approved: "bg-emerald-500/15 text-emerald-300",
    rejected: "bg-red-500/15 text-red-300",
  };
  return (
    <span className={"text-[9.5px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full font-semibold " + map[status]}>
      {status}
    </span>
  );
}

/* ---- Settings panel ------------------------------------------------------ */

function SettingsPanel({ cfg, onSaved }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (cfg) setForm({
      office_geo_lat: cfg.office_geo_lat ?? "",
      office_geo_lng: cfg.office_geo_lng ?? "",
      office_geofence_radius_m: cfg.office_geofence_radius_m ?? 150,
      office_ip_ranges: (cfg.office_ip_ranges || []).join("\n"),
      office_address_label: cfg.office_address_label || "",
    });
  }, [cfg]);
  const [busy, setBusy] = useState(false);
  if (!form) return null;

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        office_geo_lat: form.office_geo_lat === "" ? null : Number(form.office_geo_lat),
        office_geo_lng: form.office_geo_lng === "" ? null : Number(form.office_geo_lng),
        office_geofence_radius_m: Number(form.office_geofence_radius_m) || 150,
        office_ip_ranges: form.office_ip_ranges.split(/\n|,/).map((s) => s.trim()).filter(Boolean),
        office_address_label: form.office_address_label,
      };
      await api.patch("/office/config", body);
      toast.success("Office config saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const useMyBrowserLoc = async () => {
    try {
      const pos = await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej,
          { enableHighAccuracy: true, timeout: 8000 })
      );
      setForm((s) => ({
        ...s,
        office_geo_lat: pos.coords.latitude.toFixed(6),
        office_geo_lng: pos.coords.longitude.toFixed(6),
      }));
      toast.success("Set to your current location");
    } catch {
      toast.error("Couldn't fetch location");
    }
  };

  return (
    <div className="cs-card rounded-2xl p-6 max-w-3xl" data-testid="attendance-settings-panel">
      <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
        Office Configuration · HR
      </div>
      <h2 className="mt-1 font-display text-2xl uppercase tracking-tight">
        Where do you check in?
      </h2>

      <div className="mt-6 space-y-5">
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Address Label</label>
          <input value={form.office_address_label}
            onChange={(e) => setForm((s) => ({ ...s, office_address_label: e.target.value }))}
            placeholder="Mumbai HQ · Andheri West"
            className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
            data-testid="settings-address-label" />
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Latitude</label>
            <input value={form.office_geo_lat}
              onChange={(e) => setForm((s) => ({ ...s, office_geo_lat: e.target.value }))}
              placeholder="19.076000"
              className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm tabular-nums"
              data-testid="settings-lat" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Longitude</label>
            <input value={form.office_geo_lng}
              onChange={(e) => setForm((s) => ({ ...s, office_geo_lng: e.target.value }))}
              placeholder="72.877700"
              className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm tabular-nums"
              data-testid="settings-lng" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Radius (m)</label>
            <input type="number" min={50} max={1000} step={10}
              value={form.office_geofence_radius_m}
              onChange={(e) => setForm((s) => ({ ...s, office_geofence_radius_m: e.target.value }))}
              className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm tabular-nums"
              data-testid="settings-radius" />
          </div>
        </div>
        <button onClick={useMyBrowserLoc}
          className="text-[10.5px] tracking-[0.2em] uppercase text-white/60 hover:text-white underline underline-offset-4"
          data-testid="settings-use-my-loc">
          Set to my current location
        </button>

        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
            Office IP Whitelist · one per line (accepts CIDR)
          </label>
          <textarea value={form.office_ip_ranges}
            onChange={(e) => setForm((s) => ({ ...s, office_ip_ranges: e.target.value }))}
            className="mt-1 w-full h-28 bg-black/40 border border-white/15 rounded-md px-3 py-2 text-sm resize-none font-mono tabular-nums"
            placeholder={"49.207.176.0/24\n103.10.20.15"}
            data-testid="settings-ips" />
          <div className="mt-1 text-[10.5px] text-white/45 leading-relaxed">
            Staff who log in on desktop / laptop from any of these IPs will be
            auto-attended on load. Ask your IT provider or `whatismyip.com` on
            the office network to grab the range.
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button onClick={save} disabled={busy}
          className="h-10 px-6 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold bg-white text-black hover:bg-white/90 disabled:opacity-60"
          data-testid="settings-save">
          {busy ? "Saving…" : "Save Config"}
        </button>
      </div>
    </div>
  );
}

/* ---- utils --------------------------------------------------------------- */

function fmtT(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
}
function mins(a, b) {
  if (!a || !b) return "";
  const m = Math.max(0, Math.round((new Date(b) - new Date(a)) / 60000));
  const h = Math.floor(m / 60), mm = m % 60;
  return h ? `${h}h ${mm}m` : `${mm}m`;
}

/* ---- Branches panel (multi-metro) --------------------------------------- */

function BranchesPanel() {
  const [branches, setBranches] = useState([]);
  const [metros, setMetros]     = useState([]);
  const [showAdd, setShowAdd]   = useState(false);
  const [editing, setEditing]   = useState(null);

  const load = useCallback(async () => {
    const [{ data: b }, { data: m }] = await Promise.all([
      api.get("/office/branches"),
      api.get("/meta/metros"),
    ]);
    setBranches(b.branches || []);
    setMetros(m.metros || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-6 cs-card rounded-2xl p-6 max-w-3xl"
         data-testid="attendance-branches-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
            Multi-branch · Delhi · Mumbai · Bangalore · Chennai · Kolkata · Ahmedabad · Hyderabad · Pune
          </div>
          <h2 className="mt-1 font-display text-2xl uppercase tracking-tight">
            Branches ({branches.length})
          </h2>
          <p className="mt-1 text-[11.5px] text-white/50 max-w-md">
            Each branch has its own geofence + IP whitelist. Staff attendance
            rules resolve from the branch they&apos;re assigned to.
          </p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="h-9 px-4 rounded-full text-[10.5px] tracking-[0.18em] uppercase font-semibold border border-white/25 hover:bg-white hover:text-black transition inline-flex items-center gap-1.5"
          data-testid="branches-add-btn">
          <Plus size={12} /> Add Branch
        </button>
      </div>

      <div className="mt-5 space-y-2">
        {branches.map((b) => (
          <div key={b.id} className="rounded-xl border border-white/10 bg-black/25 p-3 flex items-center gap-3 flex-wrap"
               data-testid={`branch-row-${b.id}`}>
            <div className="w-10 h-10 rounded-lg bg-white/8 flex items-center justify-center text-white/85">
              <Building2 size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-display text-base uppercase tracking-tight">{b.label}</div>
                <span className="px-2 py-0.5 rounded-full text-[9.5px] tracking-[0.22em] uppercase font-semibold bg-white/8 text-white/70">
                  {b.city_label}
                </span>
                {b.is_headquarters && (
                  <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.22em] uppercase font-semibold bg-amber-500/15 text-amber-300 inline-flex items-center gap-1"
                        data-testid={`branch-hq-${b.id}`}>
                    <Star size={10} /> HQ
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-[10.5px] text-white/50">
                {b.address || "— address —"}
                <span className="text-white/30"> · </span>
                {b.office_geo_lat ? `${b.office_geo_lat.toFixed(4)}, ${b.office_geo_lng.toFixed(4)}` : "no coords"}
                <span className="text-white/30"> · </span>
                {b.office_geofence_radius_m}m
                <span className="text-white/30"> · </span>
                {(b.office_ip_ranges || []).length} IP{(b.office_ip_ranges || []).length === 1 ? "" : "s"}
              </div>
            </div>
            <button onClick={() => setEditing(b)}
              className="h-8 px-3 rounded-md text-[10px] tracking-[0.2em] uppercase font-semibold border border-white/20 hover:bg-white/8"
              data-testid={`branch-edit-${b.id}`}>
              Edit
            </button>
            {branches.length > 1 && (
              <button onClick={async () => {
                if (!window.confirm(`Delete "${b.label}"?`)) return;
                try {
                  await api.delete(`/office/branches/${b.id}`);
                  toast.success("Deleted");
                  load();
                } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
              }}
                className="p-2 rounded-md hover:bg-red-500/15 text-white/50 hover:text-red-300"
                data-testid={`branch-delete-${b.id}`}>
                <Trash2 size={13} />
              </button>
            )}
          </div>
        ))}
      </div>

      {showAdd && (
        <BranchModal metros={metros}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }} />
      )}
      {editing && (
        <BranchModal branch={editing} metros={metros}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function BranchModal({ branch, metros, onClose, onSaved }) {
  const isEdit = !!branch;
  const [form, setForm] = useState({
    city_key:        branch?.city_key || "mumbai",
    custom_city:     branch?.custom_city || "",
    label:           branch?.label || "",
    address:         branch?.address || "",
    is_headquarters: branch?.is_headquarters || false,
    office_geo_lat:  branch?.office_geo_lat ?? "",
    office_geo_lng:  branch?.office_geo_lng ?? "",
    office_geofence_radius_m: branch?.office_geofence_radius_m || 150,
    office_ip_ranges: (branch?.office_ip_ranges || []).join("\n"),
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const ipList = form.office_ip_ranges.split(/\n|,/).map((s) => s.trim()).filter(Boolean);
      if (isEdit) {
        await api.patch(`/office/branches/${branch.id}`, {
          label: form.label, address: form.address,
          is_headquarters: form.is_headquarters,
          office_geo_lat: form.office_geo_lat === "" ? null : Number(form.office_geo_lat),
          office_geo_lng: form.office_geo_lng === "" ? null : Number(form.office_geo_lng),
          office_geofence_radius_m: Number(form.office_geofence_radius_m) || 150,
          office_ip_ranges: ipList,
        });
      } else {
        await api.post("/office/branches", {
          city_key: form.city_key, custom_city: form.custom_city,
          label: form.label, address: form.address,
          is_headquarters: form.is_headquarters,
        });
      }
      toast.success(isEdit ? "Branch updated" : "Branch added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4"
         data-testid="branch-modal">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative z-10 w-full max-w-lg cs-card rounded-2xl p-6 my-8">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
          {isEdit ? "Edit Branch" : "New Branch"}
        </div>
        <div className="mt-0.5 font-display text-xl uppercase tracking-tight">
          {isEdit ? form.label : "Where's this office?"}
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">City</label>
            <select value={form.city_key} onChange={(e) => upd("city_key", e.target.value)}
              disabled={isEdit}
              className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm disabled:opacity-50"
              data-testid="branch-city-key">
              {metros.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          {form.city_key === "other" && (
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Custom City</label>
              <input value={form.custom_city} onChange={(e) => upd("custom_city", e.target.value)}
                disabled={isEdit}
                className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm disabled:opacity-50"
                data-testid="branch-custom-city" />
            </div>
          )}
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Label</label>
            <input value={form.label} onChange={(e) => upd("label", e.target.value)}
              placeholder="e.g. Andheri West HQ"
              className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
              data-testid="branch-label" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Address</label>
            <input value={form.address} onChange={(e) => upd("address", e.target.value)}
              className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
              data-testid="branch-address" />
          </div>

          {isEdit && (
            <>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Latitude</label>
                  <input value={form.office_geo_lat}
                    onChange={(e) => upd("office_geo_lat", e.target.value)}
                    className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm tabular-nums"
                    data-testid="branch-lat" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Longitude</label>
                  <input value={form.office_geo_lng}
                    onChange={(e) => upd("office_geo_lng", e.target.value)}
                    className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm tabular-nums"
                    data-testid="branch-lng" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Radius m</label>
                  <input type="number" min={50} max={1000} step={10}
                    value={form.office_geofence_radius_m}
                    onChange={(e) => upd("office_geofence_radius_m", e.target.value)}
                    className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm tabular-nums"
                    data-testid="branch-radius" />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                  IP Whitelist · one per line (CIDR OK)
                </label>
                <textarea value={form.office_ip_ranges}
                  onChange={(e) => upd("office_ip_ranges", e.target.value)}
                  className="mt-1 w-full h-24 bg-black/40 border border-white/15 rounded-md px-3 py-2 text-sm resize-none font-mono tabular-nums"
                  data-testid="branch-ip-ranges" />
              </div>
            </>
          )}

          <label className="flex items-center gap-2 text-[12px] text-white/70"
                 data-testid="branch-hq-wrap">
            <input type="checkbox" checked={form.is_headquarters}
                   onChange={(e) => upd("is_headquarters", e.target.checked)}
                   data-testid="branch-hq-checkbox" />
            Set as Headquarters
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="h-10 px-4 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold border border-white/25 hover:bg-white/8">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            className="h-10 px-6 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold bg-white text-black hover:bg-white/90 disabled:opacity-60"
            data-testid="branch-submit">
            {busy ? "Saving…" : (isEdit ? "Save" : "Add")}
          </button>
        </div>
      </form>
    </div>
  );
}
